/**
 * c-image-popup — 画像ポップアップ + 左端ミニタブ
 *
 * 状態遷移 (localStorage キー: c-image-popup:{sectionId}):
 *   (なし)                     → 初回。apparition-delay 秒後にポップアップ表示
 *   { state: 'minimized' }     → ポップアップ×後。ミニタブのみ表示、クリックで再表示
 *   { state: 'dismissed',
 *     hideUntil: timestamp }   → ミニタブ×後。hideUntil まで何も表示しない
 *
 * テーマエディタ (designMode) ではセクション選択で表示・選択解除でミニタブ化し、
 * localStorage には書き込まない。
 */
if (!customElements.get('c-image-popup')) {
  customElements.define(
    'c-image-popup',
    class CImagePopup extends HTMLElement {
      connectedCallback() {
        this.modal = this.querySelector('.c-image-popup__modal');
        this.dialog = this.querySelector('.c-image-popup__dialog');
        this.minitab = this.querySelector('.c-image-popup__minitab');
        if (!this.modal || !this.minitab) return;

        this.querySelector('.c-image-popup__close').addEventListener('click', () => this.minimize());
        this.querySelector('.c-image-popup__overlay').addEventListener('click', () => this.minimize());
        this.querySelector('.c-image-popup__minitab-open').addEventListener('click', () => this.open());
        this.querySelector('.c-image-popup__minitab-close').addEventListener('click', () => this.dismiss());
        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE' && !this.modal.hidden) this.minimize();
        });

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:section:select', (event) => {
            if (event.target.contains(this)) this.open();
          });
          document.addEventListener('shopify:section:deselect', (event) => {
            if (event.target.contains(this)) this.minimize();
          });
        } else {
          this.applyStoredState();
        }
      }

      disconnectedCallback() {
        if (this.appearTimer) clearTimeout(this.appearTimer);
        if (!this.modal || this.modal.hidden) return;
        document.body.classList.remove('overflow-hidden');
      }

      get apparitionDelay() {
        return parseInt(this.dataset.apparitionDelay || '0', 10) * 1000;
      }

      get hideDays() {
        return parseInt(this.dataset.hideDays || '0', 10);
      }

      get storageKey() {
        return `c-image-popup:${this.dataset.sectionId || 'default'}`;
      }

      readState() {
        try {
          return JSON.parse(localStorage.getItem(this.storageKey));
        } catch {
          return null;
        }
      }

      writeState(state) {
        if (window.Shopify && window.Shopify.designMode) return;
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch {
          /* localStorage 無効環境では毎回初回表示になる (安全側) */
        }
      }

      clearState() {
        try {
          localStorage.removeItem(this.storageKey);
        } catch {
          /* noop */
        }
      }

      applyStoredState() {
        const state = this.readState();
        if (state && state.state === 'dismissed') {
          if (Date.now() < (state.hideUntil || 0)) return;
          this.clearState();
        } else if (state && state.state === 'minimized') {
          this.minitab.hidden = false;
          return;
        }
        this.appearTimer = setTimeout(() => this.open(), this.apparitionDelay);
      }

      open() {
        this.minitab.hidden = true;
        this.modal.hidden = false;
        document.body.classList.add('overflow-hidden');
        if (typeof trapFocus === 'function') trapFocus(this.modal, this.dialog);
      }

      closeModal() {
        this.modal.hidden = true;
        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') removeTrapFocus();
      }

      minimize() {
        if (this.appearTimer) clearTimeout(this.appearTimer);
        this.closeModal();
        this.minitab.hidden = false;
        this.querySelector('.c-image-popup__minitab-open').focus();
        this.writeState({ state: 'minimized' });
      }

      dismiss() {
        this.closeModal();
        this.minitab.hidden = true;
        this.writeState({
          state: 'dismissed',
          hideUntil: Date.now() + this.hideDays * 86400 * 1000,
        });
      }
    }
  );
}
